import FormWrapper from "../common/FormWrapper";
import FormField from "../common/FormField";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { LinkedFieldSchema } from "../../utils/schemas/initial";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import api from "../../../../bmr/services/api/axios.config";
import { fetchTemplates } from "../../../../bmr/slices/formTemplate";
import { addLinkedField, getLinkedField } from "../../slices/FbProperty";
import { notify } from "../../../../bmr/utils/methods/notification";

const LinkedFieldForm = ({ fieldId }) => {
  const PAGE_SIZE = 10;
  const dispatch = useDispatch();

  const [templates, setTemplates] = useState([]);
  const [versionOptions, setVersionOptions] = useState([]);
  const [linkedFieldOptions, setLinkedFieldOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const linkedFieldData = useSelector(
    (state) => state.bmrFbProperty.linkedField
  );

  const {
    control,
    handleSubmit,
    setValue: formSetValue,
    reset,
  } = useForm({
    defaultValues: LinkedFieldSchema,
  });

  const linkedFieldTemplate = useWatch({
    control,
    name: `linked_field.template_id`,
  });

  const linkedFieldSection = useWatch({
    control,
    name: `linked_field.section_uuid`,
  });

  useEffect(() => {
    if (linkedFieldTemplate) {
      generateSectionOptions(linkedFieldTemplate);
    }
  }, [linkedFieldTemplate]);

  useEffect(() => {
    if (linkedFieldSection) {
      formSetValue(`linked_field.linked_field`, null);
    }
  }, [linkedFieldSection, formSetValue]);

  useEffect(() => {
    loadTemplates(1);
    fetchLinkedField();
  }, [fieldId]);

  useEffect(() => {
    if (linkedFieldData) {
      handleTemplateChange(linkedFieldData?.template_id, linkedFieldData);
      reset({
        linked_field: {
          template_id: linkedFieldData?.template_id,
          template_version: linkedFieldData?.template_version,
          section_uuid: linkedFieldData?.section_uuid,
          linked_field: linkedFieldData?.linked_field,
          default_val: linkedFieldData?.default_val,
        },
      });
    }
  }, [linkedFieldData]);

  const generateSectionOptions = async (id) => {
    const response = await api.get(`/ftb/templates/${id}/sections`);
    const sections = response.data.data?.sections;
    let secOptions = [];
    let fieldOptions = [];
    sections.forEach((sec) => {
      secOptions.push({
        label: sec.name,
        value: sec.section_uuid,
        section_uuid: sec.section_uuid,
        section_name: sec.name,
        section_order: sec.order,
      });
      sec.fields.forEach((fieldEle) => {
        if(fieldEle.input_uuid !== fieldId){
          const label = `${sec.order}.${fieldEle.order} ${fieldEle.label} (${fieldEle.property?.field_type})`;
          fieldOptions.push({
            label,
            value: fieldEle.input_uuid,
            section_uuid: sec.section_uuid,
          });
        }
      });
    });
    setSectionOptions(secOptions);
    setLinkedFieldOptions(fieldOptions);
  };

  const fetchLinkedField = async () => {
    if (fieldId) {
      dispatch(getLinkedField({ fieldId: fieldId }));
    }
  };

  const loadTemplates = async (pageNum) => {
    try {
      const res = await dispatch(
        fetchTemplates({ page: pageNum, per_page: PAGE_SIZE })
      ).unwrap();
      const newItems = res?.data?.items || [];
      if (pageNum === 1) {
        setTemplates(newItems);
      } else {
        setTemplates((prev) => [...prev, ...newItems]);
      }

      if (newItems.length === 0 || newItems.length < PAGE_SIZE) {
        setHasMore(false);
      }
    } catch (err) {
      console.error(err);
      setHasMore(false);
    }
  };

  const fetchMoreData = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadTemplates(nextPage);
  };

  const handleTemplateChange = async (id, linkedFieldData = null) => {
    let template = templates.find((tempItem) => tempItem.id === id);
    if (!template && linkedFieldData) {
      template = {
        id: linkedFieldData?.template_id,
        name: linkedFieldData?.template_name,
        version: linkedFieldData?.template_version,
        versions: [{ version: linkedFieldData?.template_version }],
      };
      setTemplates((prev) => [...[template], ...prev]);
    }
    const versionOptions = template.versions.map((t) => {
      return { label: `V.${t.version}`, value: t.version };
    });
    setVersionOptions(versionOptions);
    formSetValue(`linked_field.template_version`, template.version);
    generateSectionOptions(id);
    // formSetValue(`linked_field.template_id`, id);
    formSetValue(`linked_field.section_uuid`, null);
    formSetValue(`linked_field.linked_field`, null);
  };

  const onSubmit = async (data) => {
    const payload = {
      linked_field: data?.linked_field.linked_field,
      default_val: data?.linked_field.default_val,
    };
    const res = await dispatch(
      addLinkedField({
        fieldId,
        data: payload,
      })
    ).unwrap();
    notify.success(res?.message)
    dispatch(getLinkedField({ fieldId }));
  };

  const inputOptions =
    linkedFieldOptions?.length > 0 && linkedFieldSection
      ? linkedFieldOptions.filter(
          (item) => item.section_uuid === linkedFieldSection
        )
      : [];

  return (
    <div className="linked-field-form">
      <FormWrapper
        onSubmit={handleSubmit(onSubmit)}
        defaultValues={LinkedFieldSchema}
        control={control}
      >
        <Box>
          <Controller
            name={`linked_field.template_id`}
            rules={{ required: "Template is required" }}
            control={control}
            render={({ field, fieldState }) => {
              const { error } = fieldState;
              const hasError = !!error;

              const handleScroll = (event) => {
                const bottom =
                  Math.round(
                    event.target.scrollHeight - event.target.scrollTop
                  ) === event.target.clientHeight;
                if (bottom && hasMore) {
                  fetchMoreData();
                }
              };

              return (
                <Box sx={{ marginBottom: ".5rem" }}>
                  <Typography variant="p" className="inline-block">
                    Template
                  </Typography>
                  <FormControl
                    fullWidth
                    error={hasError}
                    className="controlledSelect"
                  >
                    <Select
                      {...field}
                      sx={{
                        "& fieldset": { border: "1px solid #F0F1F5" },
                      }}
                      displayEmpty
                      value={field.value ?? ""}
                      placeholder="Select"
                      onChange={(event) => {
                        field.onChange(event);
                        handleTemplateChange(event.target.value);
                      }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        PaperProps: {
                          style: {
                            maxHeight: "200px",
                            minWidth: "200px",
                            overflow: "auto",
                          },
                          onScroll: handleScroll,
                        },
                        disablePortal: true,
                      }}
                    >
                      <MenuItem disabled value="">
                        Select
                      </MenuItem>
                      {templates.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {hasError && (
                      <FormHelperText>{error?.message}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
              );
            }}
          />

          <Controller
            name={`linked_field.template_version`}
            control={control}
            rules={{ required: "Template version is required" }}
            render={({ field, fieldState }) => {
              const { error } = fieldState;
              const hasError = !!error;
              return (
                <FormControl
                  fullWidth
                  error={hasError}
                  className="controlledSelect"
                >
                  <FormField
                    {...field}
                    // disabled
                    inputLabelTop="Template Version"
                    type="select"
                    options={versionOptions}
                    placeholder={"Select"}
                  />
                  {hasError && (
                    <FormHelperText>{error?.message}</FormHelperText>
                  )}
                </FormControl>
              );
            }}
          />
          <Controller
            name={`linked_field.section_uuid`}
            control={control}
            rules={{ required: "Section is required" }}
            render={({ field, fieldState }) => {
              const { error } = fieldState;
              const hasError = !!error;
              return (
                <FormControl
                  fullWidth
                  error={hasError}
                  className="controlledSelect"
                >
                  <FormField
                    {...field}
                    inputLabelTop="Section"
                    type="select"
                    options={sectionOptions}
                    placeholder={"Select"}
                  />
                  {hasError && (
                    <FormHelperText>{error?.message}</FormHelperText>
                  )}
                </FormControl>
              );
            }}
          />

          <Controller
            name={`linked_field.linked_field`}
            control={control}
            rules={{ required: "Linked Field is required" }}
            render={({ field, fieldState }) => {
              const { error } = fieldState;
              const hasError = !!error;
              return (
                <FormControl
                  fullWidth
                  error={hasError}
                  className="controlledSelect"
                >
                  <FormField
                    {...field}
                    inputLabelTop="Linked to field"
                    type="select"
                    options={inputOptions}
                    placeholder={"Select"}
                    description={
                      "Source field from which to pull the input value"
                    }
                  />
                  {hasError && (
                    <FormHelperText>{error?.message}</FormHelperText>
                  )}
                </FormControl>
              );
            }}
          />
          <Controller
            name={`linked_field.default_val`}
            control={control}
            render={({ field }) => {
              return (
                <FormField
                  {...field}
                  inputLabelTop="Default Value (if required)"
                  placeholder={"Enter Default Message"}
                  type="number"
                   value={field.value ?? 0}
                />
              );
            }}
          />
        </Box>
        <Box className="fixed bottom-0 left-0 w-full bg-red-300 p-4 shadow-md mt-2">
          <Button
            variant="contained"
            size="medium"
            type="submit"
            sx={{
              backgroundColor: "#3571FF",
              padding: "0.5rem 1rem",
              borderRadius: "0.625rem",
              marginTop: "1rem",
            }}
          >
            Submit
          </Button>
        </Box>
      </FormWrapper>
    </div>
  );
};

export default LinkedFieldForm;
